import { RootStore } from 'store/RootStore'
import { EDITOR_MONITOR_STORE } from 'store/EditorMonitorStore'
import { IaAction, IaActionTypes, InteractiveAreaAPI } from 'models'
import { EDITOR_INTERACTIVE_AREAS_STORE } from 'store/EditorInteractiveAreasStore'
import { GENERAL_STORE } from 'store/GeneralStore'
import { Actions } from './types'

export const EDITOR_ACTION_HISTORY_STORE = 'EDITOR_ACTION_HISTORY_STORE'

export class EditorActionHistoryStore {
  private actions: Actions = {}
  private undoStack: string[] = []
  private redoStack: string[] = []
  private actionInProgress: boolean = false

  rootStore: RootStore

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  private get undoStackLength () {
    return (this.undoStack || []).length
  }

  private get lastActionInUndoStack () {
    const index = this.undoStackLength - 1
    const id = index < 0
      ? undefined
      : this.undoStack[index]
    return id
      ? this.actions[id]
      : undefined
  }

  private get redoStackLength () {
    return (this.redoStack || []).length
  }

  private get lastActionInRedoStack () {
    const index = this.redoStackLength - 1
    const id = index < 0
      ? undefined
      : this.redoStack[index]
    return id
      ? this.actions[id]
      : undefined
  }

  private get actionsList () {
    return Object.values(this.actions)
  }

  clearRedoStack = () => {
    if (!this.redoStack.length) return
    this.redoStack = []
  }

  saveIaAction = (action: IaAction) => {
    this.actionInProgress = true
    this.actions[action.id] = action
    this.undoStack.push(action.id)
    this.clearRedoStack()
    this.logStacks()
  }

  setNoActionInProgress = () => {
    this.actionInProgress = false
  }

  undo = () => {
    const {
      [EDITOR_MONITOR_STORE]: { deleteInteractiveArea },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { saveIA },
      [GENERAL_STORE]: { showInfo, showSuccess }
    } = this.rootStore

    if (this.actionInProgress) {
      showInfo('Another action is in progress. Please try again.')
      return
    }

    const action = this.lastActionInUndoStack

    if (!action) {
      showInfo('Nothing to undo.')
      return
    }

    const next = () => {
      this.undoStack.pop()
      this.redoStack.push(action.id)
      showSuccess(`UNDO: ${action.type}`)
      this.setNoActionInProgress()
      this.logStacks()
    }

    this.actionInProgress = true

    switch (action.type) {
      case IaActionTypes.CREATE: {
        deleteInteractiveArea(action.afterVersion.id, action)
          .then(next)
        break
      }
      case IaActionTypes.DELETE: {
        saveIA(action.afterVersion).then(action.setBeforeVersion).then(next)
        break
      }
      case IaActionTypes.MARK_POINT:
      case IaActionTypes.ATTACH_ENTITY:
      case IaActionTypes.DELETE_FROM_SHOT:
      case IaActionTypes.DETACH_ENTITY:
      case IaActionTypes.CHANGE:
      case IaActionTypes.PASTE: {
        saveIA(action.beforeVersion).then(next)
        break
      }

      default: {
        this.actionInProgress = false
        showInfo(`Unrecognized action type: <${action.type}>`)
        break
      }
    }
  }

  redo = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: { saveIA },
      [EDITOR_MONITOR_STORE]: { deleteInteractiveArea },
      [GENERAL_STORE]: { showInfo, showSuccess }
    } = this.rootStore

    if (this.actionInProgress) {
      showInfo('Another action is in progress. Please try again.')
      return
    }

    const action = this.lastActionInRedoStack

    if (!action) {
      showInfo('Nothing to redo.')
      return
    }

    const next = () => {
      this.redoStack.pop()
      this.undoStack.push(action.id)
      showSuccess(`REDO: ${action.type}`)
      this.setNoActionInProgress()
      this.logStacks()
    }

    const handleIaRecreation = ({ id: newId }: InteractiveAreaAPI) => {
      this.actionsList
        .filter(a => (a.id !== action.id) && (a.iaId === action.beforeVersion.id))
        .forEach(a => a.updateIaId(newId))

      next()
    }

    this.actionInProgress = true

    switch (action.type) {
      case IaActionTypes.CREATE: {
        saveIA(action.afterVersion, action).then(handleIaRecreation)
        break
      }
      case IaActionTypes.DELETE: {
        deleteInteractiveArea(action.beforeVersion.id, action).then(next)
        break
      }
      case IaActionTypes.DELETE_FROM_SHOT:
      case IaActionTypes.MARK_POINT:
      case IaActionTypes.ATTACH_ENTITY:
      case IaActionTypes.DETACH_ENTITY:
      case IaActionTypes.CHANGE:
      case IaActionTypes.PASTE: {
        saveIA(action.afterVersion).then(next)
        break
      }

      default: {
        this.actionInProgress = false
        showInfo(`Unrecognized action type: <${action.type}>`)
        break
      }
    }
  }

  logStacks = () => {
    console.groupCollapsed(
      `Action log: 
    UNDO: ${this.undoStackLength}, 
    REDO: ${this.redoStackLength}`
    )
    console.log('UNDO STACK', this.undoStack)
    console.log('REDO STACK', this.redoStack)
    console.log('ACTIONS', this.actions)
    console.groupEnd()
  }
}
